import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import type { ClavePermiso } from '@libreta/contratos';
import { Marco } from './componentes/Marco';
import { Cargando, Vacio } from './componentes/Piezas';
import { PaginaIngreso } from './paginas/PaginaIngreso';
import { useSesion } from './sesion/sesion';

const PaginaTablero = lazy(() => import('./paginas/PaginaTablero'));
const PaginaClientes = lazy(() => import('./paginas/PaginaClientes'));
const PaginaInventario = lazy(() => import('./paginas/PaginaInventario'));
const PaginaVentas = lazy(() => import('./paginas/PaginaVentas'));
const PaginaNuevaVenta = lazy(() => import('./paginas/PaginaNuevaVenta'));
const PaginaCobranzas = lazy(() => import('./paginas/PaginaCobranzas'));
const PaginaRecibo = lazy(() => import('./paginas/PaginaRecibo'));
const PaginaFacturas = lazy(() => import('./paginas/PaginaFacturas'));
const PaginaConfiguracion = lazy(() => import('./paginas/PaginaConfiguracion'));
const PaginaAuditoria = lazy(() => import('./paginas/PaginaAuditoria'));
const PaginaPlataforma = lazy(() => import('./paginas/PaginaPlataforma'));
const PaginaMiCuenta = lazy(() => import('./paginas/PaginaMiCuenta'));
const PaginaNoIncluido = lazy(() => import('./paginas/PaginaNoIncluido'));

function Requiere({ permisos, children }: { permisos: ClavePermiso[]; children: ReactNode }) {
  const { estado } = useSesion();
  if (estado.tipo !== 'adentro') return null;
  if (!permisos.some((p) => estado.yo.permisos.includes(p))) {
    return <Vacio icono="candado" titulo="Tu rol no tiene acceso a esta sección" texto="Si lo necesitás, pedíselo a quien administra los roles de tu entidad." />;
  }
  return <>{children}</>;
}

export function App() {
  const { estado } = useSesion();

  if (estado.tipo === 'cargando') {
    return <div className="contenido"><Cargando filas={6} /></div>;
  }
  if (estado.tipo === 'afuera') return <PaginaIngreso />;

  const { yo } = estado;
  // El recibo se imprime sin el marco de la aplicación.
  return (
    <Suspense fallback={<div className="contenido"><Cargando filas={6} /></div>}>
      <Routes>
        <Route path="/recibos/:id/imprimir" element={<Requiere permisos={['recibos.ver', 'pagos.registrar']}><PaginaRecibo /></Requiere>} />
        <Route
          path="*"
          element={
            <Marco>
              <Suspense fallback={<Cargando filas={6} />}>
                {yo.entidad ? (
                  <Routes>
                    <Route path="/" element={<Requiere permisos={['tablero.ver']}><PaginaTablero /></Requiere>} />
                    <Route path="/clientes/*" element={<Requiere permisos={['clientes.ver']}><PaginaClientes /></Requiere>} />
                    <Route path="/inventario/*" element={<Requiere permisos={['productos.ver', 'categorias.gestionar']}><PaginaInventario /></Requiere>} />
                    <Route path="/ventas" element={<Requiere permisos={['ventas.ver']}><PaginaVentas /></Requiere>} />
                    <Route path="/ventas/nueva" element={<Requiere permisos={['ventas.crear']}><PaginaNuevaVenta /></Requiere>} />
                    <Route path="/ventas/:id" element={<Requiere permisos={['ventas.ver', 'pagos.registrar']}><PaginaVentas /></Requiere>} />
                    <Route path="/cobranzas/*" element={<Requiere permisos={['pagos.ver', 'pagos.registrar', 'recibos.ver']}><PaginaCobranzas /></Requiere>} />
                    <Route path="/facturas" element={<Requiere permisos={['facturas.ver']}><PaginaFacturas /></Requiere>} />
                    <Route path="/configuracion/*" element={<Requiere permisos={['entidad.configurar', 'medios.gestionar', 'planes.gestionar', 'usuarios.gestionar', 'roles.gestionar']}><PaginaConfiguracion /></Requiere>} />
                    <Route path="/auditoria" element={<Requiere permisos={['auditoria.ver']}><PaginaAuditoria /></Requiere>} />
                    <Route path="/no-incluido/:modulo" element={<PaginaNoIncluido />} />
                    <Route path="/mi-cuenta" element={<PaginaMiCuenta />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                ) : (
                  <Routes>
                    <Route path="/" element={<PaginaPlataforma />} />
                    <Route path="/mi-cuenta" element={<PaginaMiCuenta />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                )}
              </Suspense>
            </Marco>
          }
        />
      </Routes>
    </Suspense>
  );
}
